/**
 * Phones get a curated one-or-two-row toolbar instead of the full one.
 *
 * The `toolbarGroups` config below produces about seven rows of buttons on a
 * 390px screen, which buries the text area under its own chrome. Capping the
 * height with a scrollbar would only hide the buttons; picking the handful
 * people actually use on a phone keeps every visible button useful. Source is
 * kept so power users can still fix markup, and the full toolbar is one
 * rotation (or one desktop visit) away.
 *
 * CKEditor reads `toolbar` in preference to `toolbarGroups`, so returning an
 * empty object on desktop leaves the existing grouped toolbar untouched.
 */
const COMPACT_TOOLBAR = [
    { name: 'clipboard', items: ['Undo', 'Redo'] },
    { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'RemoveFormat'] },
    { name: 'paragraph', items: ['NumberedList', 'BulletedList'] },
    { name: 'links', items: ['Link', 'Unlink'] },
    { name: 'insert', items: ['Image', 'Table'] },
    { name: 'styles', items: ['Format'] },
    { name: 'document', items: ['Source'] },
];

export const EDITOR_COMPACT_BREAKPOINT = 768;

export const isCompactEditor = () =>
    typeof window !== 'undefined' &&
    window.innerWidth <= EDITOR_COMPACT_BREAKPOINT;

/**
 * Spread into any CKEditor config to collapse its toolbar on small screens:
 *   const editorConfig = { ...myConfig, ...compactToolbar() };
 *
 * Evaluated when the config object is built. Configs defined at module scope
 * therefore settle at page load, which is fine — CKEditor cannot re-lay-out an
 * existing instance's toolbar without re-creating the editor anyway.
 */
export const compactToolbar = () =>
    (isCompactEditor() ? { toolbar: COMPACT_TOOLBAR } : {});

export const getEditorConfig = (client, axios) => {
    const editorConfig = {
        extraPlugins: ["tableresize", "uploadimage", "createpdf"],
        removePlugins: [
            "exportpdf",
            "forms",
            "smiley",
            "language",
            "iframe",
            "about",
        ],
        // filebrowserBrowseUrl: 'http://localhost:4000/media/24b358cf-8e56-4b03-9843-29fabf589b8d/template/home-advt_jzx7KBU.jpeg',
        filebrowserImageUploadUrl:
            process.env.REACT_APP_BASE_URL +
            `/template/upload_template_image/`,
        fileTools_requestHeaders: axios.defaults.headers.common,
        uploadUrl:
            process.env.REACT_APP_BASE_URL +
            `/template/upload_template_image/`,
        contentsCss: [
            // "https://cdn.jsdelivr.net/npm/bootstrap@4.3.1/dist/css/bootstrap.min.css",
            process.env.REACT_APP_BASE_URL +
            "/staticfiles/ckeditor/4.19.0/full-all/document-style.css",
            "/ckeditor-content.css",
        ],
        bodyClass: "document-editor",
        // height: 900,
        // width: 1000,
        // old height and width
        // height: "15.8cm",
        // width: "21cm",
        // mathi's height and width
        height: "59vh",
        width: "100%",
        allowedContent: true,
        // extraPlugins: [
        //     'timestamp'
        // ]
        // exportPdf_options: {
        //     header_html: '<div class="styled">This is the Header</div>',
        //     footer_html: '<div class="styled-counter"><span class="date></span></div>',
        //     header_and_footer_css: '.styled { font-weight: bold; padding: 10px; display: flex; color: red; } .styled-counter {font-size: 10px; color: red; }',
        //     margin_top: '2cm',
        //     margin_bottom: '10cm',
        // }
        // exportPdf_options: {
        //     format: "A3"
        // }
        exportPdf_options: {
            header_html: '<div class="styled">Header content</div>',
            footer_html: '<div class="styled"><span class="pageNumber"></span></div>',
            header_and_footer_css:
                ".styled { font-weight: bold; padding: 10px; text-align: center; color: red; }",
            margin_left: "1cm",
            margin_right: "2cm",
            format: "A5",
            page_orientation: "landscape",
        },
        protectedSource: [/{%[\s\S]*?%}+/g, /{{[\s\S]*?}}+/g],

        // toolbarGroups: [...CKEditor.toolbarGroups].push({ name: "ckwebspeech" }),

        // toolbarGroups: [
        //   { name: 'ckwebspeech' },  //Add the CKWebSpeech button on the toolbar
        // ],

        toolbarGroups: [
            { name: 'document', groups: ['mode', 'document', 'doctools'] },
            { name: 'clipboard', groups: ['clipboard', 'undo'] },
            { name: 'editing', groups: ['find', 'selection', 'spellchecker'] },
            { name: 'links' },
            { name: 'insert' },
            { name: 'forms' },
            { name: 'tools' },
            { name: 'others' },
            '/',
            { name: 'basicstyles', groups: ['basicstyles', 'cleanup'] },
            { name: 'paragraph', groups: ['list', 'indent', 'blocks', 'align', 'bidi'] },
            { name: 'styles' },
            { name: 'colors' },
            { name: 'about' },
            { name: 'ckwebspeech' },  //Add the CKWebSpeech button on the toolbar
        ],

        ckwebspeech: {
            'culture': 'en-us',
            'commandvoice': 'okay', // trigger command listener
            'commands': [            // action list
                // {'[action]': '['text to trigger action']'}
                { 'newline': 'new line' },
                { 'newparagraph': 'new paragraph' },
                { 'undo': 'undo' },
                { 'redo': 'redo' }
            ]
        },
        // formatOutput: false,
        // formatSource: false

        // Phones get a short toolbar; desktop keeps the full grouped one.
        ...compactToolbar(),
    };

    return editorConfig;
}

export const page_number_position = [
    {
        id: "top-left",
        label: "Top Left",
    },
    {
        id: "top-center",
        label: "Top Center",
    },
    {
        id: "top-right",
        label: "Top Right",
    },
    {
        id: "bottom-left",
        label: "Bottom Left",
    },
    {
        id: "bottom-center",
        label: "Bottom Center",
    },
    {
        id: "bottom-right",
        label: "Bottom Right",
    },
];

export const templateTypes = [
    { id: 1, value: "QUANTITATIVE", label: "QUANTITATIVE" },
    { id: 2, value: "QUALITATIVE", label: "QUALITATIVE" },
    { id: 3, value: "DESCRIPTIVE", label: "DESCRIPTIVE" }
]

// Letter - 8.5in x 11in (21.6cm x 27.9cm)
// Legal - 8.5in x 14in (21.6cm x 35.6cm)
// Tabloid - 11in x 17in (27.9cm x 43.2cm)
// Ledger - 17in x 11in (43.2cm x 27.9cm)
// A0 - 33.1in x 46.8in (84.1cm x 118.9cm)
// A1 - 23.4in x 33.1in (59.4cm x 84.1cm)
// A2 - 16.54in x 23.4in (42.0cm x 59.4cm)
// A3 - 11.7in x 16.54in (29.7cm x 42.0cm)
// A4 - 8.27in x 11.7in (21.0cm x 29.7cm)
// A5 - 5.83in x 8.27in (14.8cm x 21.0cm)
// A6 - 4.13in x 5.83in (10.5cm x 14.8cm)

export const page_sizes = [
    {
        id: "A3",
        width: 29.7,
        height: 42.0,
    },
    {
        id: "A4",
        width: 21,
        height: 29.7,
    },
    {
        id: "A5",
        width: 14.8,
        height: 21.0,
    },
    {
        id: "A6",
        width: 10.5,
        height: 14.8,
    },
    {
        id: "Letter",
        width: 21.6,
        height: 27.9,
    },
    {
        id: "Barcode",
        width: 4,
        height: 1,
    },
    // {
    //     id: "Legal",
    //     width: 21.6,
    //     height: 35.6
    // }, {
    //     id: "Tabloid",
    //     width: 27.9,
    //     height: 43.2
    // }, {
    //     id: "Ledger",
    //     width: 43.2,
    //     height: 27.9
    // },
    // {
    //     id: "A0",
    //     width: 84.1,
    //     height: 118.9
    // }, {
    //     id: "A1",
    //     width: 59.4,
    //     height: 84.1
    // }, {
    //     id: "A2",
    //     width: 42.0,
    //     height: 59.4
    // }
];

export const page_orientation = [
    {
        id: "portrait",
        label: "Portrait",
    },
    {
        id: "landscape",
        label: "Landscape",
    },
];